import { useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { analyticsService } from '../services/analyticsService';

// Hook for analytics tracking
export const useAnalytics = () => {
  const location = useLocation();

  // Track page views automatically
  useEffect(() => {
    const pageName = getPageNameFromPath(location.pathname);
    analyticsService.trackPageView(pageName, location.pathname);
  }, [location.pathname]);

  // Track page load time
  useEffect(() => {
    const startTime = performance.now();
    
    const handleLoad = () => {
      const loadTime = performance.now() - startTime;
      analyticsService.trackPageLoad(loadTime);
    };

    // Track load time when component mounts
    handleLoad();

    return () => {
      // Cleanup if needed
    };
  }, []);

  // Helper function to get page name from path
  const getPageNameFromPath = (pathname: string): string => {
    if (pathname === '/') return 'Home';
    if (pathname === '/demo') return 'Demo Page';
    if (pathname === '/cafes') return 'Cafe Selection';
    if (pathname.includes('/public')) return 'Public Page';
    if (pathname.includes('/dashboard')) return 'Dashboard';
    if (pathname.includes('/menu')) return 'Menu Management';
    if (pathname.includes('/tables')) return 'Table Management';
    if (pathname.includes('/orders')) return 'Order Management';
    if (pathname.includes('/kitchen')) return 'Kitchen Cabinet';
    if (pathname.includes('/settings')) return 'Settings';
    if (pathname.includes('/table/')) return 'Table Order';
    if (pathname.includes('/order/')) return 'Order Status';
    
    return 'Unknown Page';
  };

  // Track button clicks
  const trackButtonClick = useCallback((buttonName: string) => {
    analyticsService.trackButtonClick(buttonName);
  }, []);

  // Track form submissions
  const trackFormSubmitted = useCallback((formName: string, success: boolean) => {
    analyticsService.trackFormSubmitted(formName, success);
  }, []);

  // Track errors
  const trackError = useCallback((errorType: string, errorMessage: string) => {
    analyticsService.trackError(errorType, errorMessage);
  }, []);

  // Track feature usage
  const trackFeatureUsed = useCallback((featureName: string) => {
    analyticsService.trackFeatureUsed(featureName);
  }, []);

  // Track organization events
  const trackOrganizationCreated = useCallback((orgName: string, orgUrl: string) => {
    analyticsService.trackOrganizationCreated(orgName, orgUrl);
  }, []);

  const trackOrganizationSelected = useCallback((orgName: string, orgUrl: string) => {
    analyticsService.trackOrganizationSelected(orgName, orgUrl);
  }, []);

  // Track menu events
  const trackMenuItemCreated = useCallback((itemName: string, category: string) => {
    analyticsService.trackMenuItemCreated(itemName, category);
  }, []);

  const trackMenuItemUpdated = useCallback((itemName: string, category: string) => {
    analyticsService.trackMenuItemUpdated(itemName, category);
  }, []);

  const trackMenuItemDeleted = useCallback((itemName: string, category: string) => {
    analyticsService.trackMenuItemDeleted(itemName, category);
  }, []);

  // Track order events
  const trackOrderCreated = useCallback((orderId: string, total: number, itemCount: number) => {
    analyticsService.trackOrderCreated(orderId, total, itemCount);
  }, []);

  const trackOrderStatusChanged = useCallback((orderId: string, oldStatus: string, newStatus: string) => {
    analyticsService.trackOrderStatusChanged(orderId, oldStatus, newStatus);
  }, []);

  const trackPublicOrderCreated = useCallback((orderId: string, orgUrl: string, tableId: string) => {
    analyticsService.trackPublicOrderCreated(orderId, orgUrl, tableId);
  }, []);

  // Track table events
  const trackTableCreated = useCallback((tableName: string) => {
    analyticsService.trackTableCreated(tableName);
  }, []);

  const trackTableUpdated = useCallback((tableName: string) => {
    analyticsService.trackTableUpdated(tableName);
  }, []);

  const trackTableDeleted = useCallback((tableName: string) => {
    analyticsService.trackTableDeleted(tableName);
  }, []);

  // Track user events
  const trackUserLogin = useCallback((method: string, userId?: string) => {
    analyticsService.trackUserLogin(method, userId);
  }, []);

  const trackUserLogout = useCallback(() => {
    analyticsService.trackUserLogout();
  }, []);

  // Track demo mode
  const trackDemoModeActivated = useCallback(() => {
    analyticsService.trackDemoModeActivated();
  }, []);

  return {
    trackButtonClick,
    trackFormSubmitted,
    trackError,
    trackFeatureUsed,
    trackOrganizationCreated,
    trackOrganizationSelected,
    trackMenuItemCreated,
    trackMenuItemUpdated,
    trackMenuItemDeleted,
    trackOrderCreated,
    trackOrderStatusChanged,
    trackPublicOrderCreated,
    trackTableCreated,
    trackTableUpdated,
    trackTableDeleted,
    trackUserLogin,
    trackUserLogout,
    trackDemoModeActivated,
  };
}; 